import { useState, useEffect, useRef, FC } from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from '__components/Modals/presets/BasicModal';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import TextInput from '__components/Inputs/TextInput';
import { BasicRichTextEditor } from '__components/WYSIWYG';
import { EditTextBlockModalContent } from '../styles';
import { useFlashTransition } from 'hooks/useFlashState';
import Validation from '__components/Inputs/Validation';
import { useToast, toastVariant } from '__components/ToastProvider';
import { getOriginalError } from 'services/AxiosService';
import { getErrorMessage } from 'hooks/useErrors';
import { WYSIWYGHandle } from 'components/WYSIWYG/types';
import { EditorOptions } from '@tiptap/core';

type TextBlockModalProps = {
  textBlock: {
    internalName: string;
    label: string;
    id?: string;
  };
  onHide: () => void;
  onConfirm: (data: {
    internalName: string;
    label: string;
  }) => void | Promise<void>;
  validateInternalName: (internalName: string) => string | null;
};

export const TextBlockModal: FC<TextBlockModalProps> = ({
  textBlock,
  onHide,
  onConfirm,
  validateInternalName,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const editorRef = useRef<WYSIWYGHandle>(null);

  const [internalName, setInternalName] = useState(
    textBlock.internalName || ''
  );
  const [label, setLabel] = useState(textBlock.label || '');
  const [errors, setErrors] = useState<{
    internalName?: string;
    label?: string;
  }>({});
  const [saving, setSaving] = useState(false);
  const [showConfirmExit, setShowConfirmExit] = useState(false);

  const [_, showMessage, flashErrorMessage, cancelFlash] = useFlashTransition();

  const nameError = errors.internalName;
  const labelError = errors.label;
  const validationError = nameError || labelError;

  useEffect(() => {
    if (validationError) {
      flashErrorMessage();
    } else {
      cancelFlash();
    }
  }, [validationError, flashErrorMessage, cancelFlash]);

  const dirty =
    label !== textBlock.label || internalName !== textBlock.internalName;

  const handleHide = () => {
    if (dirty) {
      setShowConfirmExit(true);
    } else {
      onHide();
    }
  };

  const handleInternalNameChange = (value: string) => {
    setInternalName(value);
    setErrors((prev) => ({ ...prev, internalName: undefined }));
  };

  const handleInternalNameBlur = () => {
    const trimmedValue = internalName.trim();

    if (!trimmedValue) {
      const message = t('Internal name field is required');
      setErrors((prev) => ({ ...prev, internalName: message }));
      return;
    }

    const validationError = validateInternalName(trimmedValue);

    if (validationError) {
      setErrors((prev) => ({ ...prev, internalName: validationError }));
    } else {
      setInternalName(trimmedValue);
    }
  };

  const handleLabelChange: EditorOptions['onUpdate'] = ({ editor }) => {
    setLabel(editor.getHTML());
    setErrors((prev) => ({ ...prev, label: undefined }));
  };

  const handleLabelBlur = () => {
    if (!label.trim()) {
      const message = t('This field is required');
      setErrors((prev) => ({ ...prev, label: message }));
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm({
        internalName,
        label,
      });
      onHide();
    } catch (err: unknown) {
      const message =
        getOriginalError(getErrorMessage(err)) ||
        t('An error occurred while saving the text block.');
      showToast({
        message,
        variant: toastVariant.FAILURE,
      });
    } finally {
      setSaving(false);
    }
  };

  const isEdit = !!textBlock.id;
  const isConfirmDisabled =
    saving || !internalName || !label || nameError || labelError;

  return (
    <>
      {showConfirmExit && (
        <ConfirmationModal
          show
          heading={t('You Have Unsaved Changes')}
          buttonText={t('Discard Changes')}
          actionBtnColor="red"
          onConfirm={() => {
            setShowConfirmExit(false);
            onHide();
          }}
          onHide={() => setShowConfirmExit(false)}
        >
          {t('Unsaved changes will be lost, would you like to continue?')}
        </ConfirmationModal>
      )}
      <BasicModal
        show
        size="medium"
        heading={isEdit ? t('Edit Text Block') : t('Add Text Block')}
        disabled={isConfirmDisabled}
        onHide={handleHide}
        onConfirm={handleConfirm}
      >
        <EditTextBlockModalContent>
          <TextInput
            data-qa-id={
              isEdit
                ? 'edit-text-block-internal-name-input'
                : 'add-text-block-internal-name-input'
            }
            inModal
            value={internalName}
            label={t('Internal Name')}
            placeholder={t('Enter Internal Name')}
            error={!!nameError}
            validate={{
              showMessage: nameError && showMessage,
              message: nameError,
            }}
            onChange={handleInternalNameChange}
            onBlur={handleInternalNameBlur}
          />
          <BasicRichTextEditor
            ref={editorRef}
            enableColor
            enableHighlight
            enableFontSize
            enableLineHeight
            enableClearFormatting
            enableHorizontalRule
            initialValue={label}
            onChange={handleLabelChange}
            error={!!labelError}
            onBlur={handleLabelBlur}
          />
          <Validation
            inModal
            target={editorRef.current?.containerRef.current}
            message={labelError}
            showMessage={!nameError && labelError && showMessage}
          />
        </EditTextBlockModalContent>
      </BasicModal>
    </>
  );
};
